import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  LogOut,
  Mail,
  Phone,
  MapPin,
  FileText,
  ArrowRight,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CURRENT_YEAR = new Date().getFullYear();

export const MemberDashboardPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [member, setMember] = useState(null);
  const [payments, setPayments] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("hafani_member_token");
    if (!token) {
      navigate("/login");
      return;
    }

    const fetchMember = async () => {
      try {
        const [memberRes, payRes, balanceRes] = await Promise.all([
          axios.get(`${API}/members/me`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API}/payments/my-history`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API}/payments/my-balance`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (!memberRes.data.association_id) {
          navigate("/choisir-communaute");
          return;
        }
        setMember(memberRes.data);
        setPayments(payRes.data);
        setBalance(balanceRes.data);
      } catch (err) {
        console.error(err);
        toast({
          title: "Session expirée",
          description: "Veuillez vous reconnecter.",
          variant: "destructive",
        });
        localStorage.removeItem("hafani_member_token");
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };

    fetchMember();
  }, [navigate, toast]);

  const handleLogout = () => {
    localStorage.removeItem("hafani_member_token");
    navigate("/login");
  };

  // Tous les versements `completed` sont conservés (des cotisations partielles
  // peuvent légitimement compter plusieurs paiements complétés pour le même
  // type+année). Seuls les intents `pending` résiduels d'un même exercice sont
  // dédoublonnés, pour ne pas polluer l'historique d'un onglet CB abandonné.
  const filteredPayments = (() => {
    const completed = payments.filter((p) => p.status === "completed");
    const pendingByKey = {};
    payments
      .filter((p) => p.status !== "completed")
      .forEach((p) => {
        const key = `${p.membership_type}-${p.year}`;
        const existing = pendingByKey[key];
        if (!existing || new Date(p.created_at) > new Date(existing.created_at)) {
          pendingByKey[key] = p;
        }
      });

    return [...completed, ...Object.values(pendingByKey)].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  })();

  const paymentState = balance
    ? balance.is_settled
      ? "paid"
      : balance.paid_amount > 0
        ? "partial"
        : "pending"
    : (member?.payment_status === "completed" ? "paid" : "pending");
  const isPaid = paymentState === "paid";
  const isPartial = paymentState === "partial";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#EAE8E4] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!member) return null;

  return (
    <div className="min-h-screen bg-[#EAE8E4] font-sans antialiased flex flex-col">

      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-slate-200/60 bg-[#EAE8E4]/90 backdrop-blur-md">
        <div className="max-w-5xl mx-auto flex h-16 items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex flex-col items-start group">
            <img
              src="/assets/logo.png"
              alt="Hafani"
              className="h-9 w-auto object-contain transition-transform group-hover:scale-105"
            />
            <span className="text-[9px] text-emerald-700 font-medium mt-0.5 tracking-wide">
              Hafani, une solution pensée pour notre fin
            </span>
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors group"
          >
            <LogOut className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            <span className="hidden sm:inline">Déconnexion</span>
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-10 lg:py-14 space-y-8">

        {/* Greeting */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 mb-2">
              Espace Particuliers
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl text-slate-900 leading-tight">
              Bonjour,{" "}
              <em className="not-italic text-emerald-700">{member.first_name}.</em>
            </h1>
            <p className="text-sm text-slate-400 font-light mt-2">
              Membre depuis {new Date(member.created_at).getFullYear()}
            </p>
          </div>

          {/* Quick status pill */}
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border self-start sm:self-auto ${
            isPaid
              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : "bg-amber-50 border-amber-200 text-amber-700"
          }`}>
            {isPaid ? (
              <><CheckCircle2 className="h-4 w-4" /> Cotisation {CURRENT_YEAR} à jour</>
            ) : isPartial ? (
              <><Clock className="h-4 w-4" /> Cotisation {CURRENT_YEAR} partielle — {(balance.remaining_amount ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € restants</>
            ) : (
              <><Clock className="h-4 w-4" /> Cotisation {CURRENT_YEAR} en attente</>
            )}
          </div>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* Profile card — 2/3 */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-100">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-6">
              Informations personnelles
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              {[
                { icon: Mail, label: "Email", value: member.email || "—" },
                { icon: Phone, label: "Téléphone", value: member.phone || "—" },
                {
                  icon: MapPin,
                  label: "Ville",
                  value: member.address?.split(",").pop()?.trim() || "—",
                },
                {
                  icon: FileText,
                  label: "Type d'adhésion",
                  value: member.membership_type || "Adhésion annuelle",
                },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-0.5">
                      {label}
                    </p>
                    <p className="text-slate-900 text-sm break-words capitalize">{value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cotisation card — 1/3 */}
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-100 flex flex-col items-center justify-between gap-6">

            {/* Year ring — rempli au prorata du taux de recouvrement */}
            <div className="flex flex-col items-center gap-3 pt-2">
              <div className={`relative h-24 w-24 rounded-full flex items-center justify-center ${
                isPaid ? "bg-emerald-50" : "bg-amber-50"
              }`}>
                <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 100 100">
                  <circle
                    cx="50" cy="50" r="44"
                    fill="none"
                    stroke={isPaid ? "#d1fae5" : "#fef3c7"}
                    strokeWidth="8"
                  />
                  <circle
                    cx="50" cy="50" r="44"
                    fill="none"
                    stroke={isPaid ? "#059669" : "#d97706"}
                    strokeWidth="8"
                    strokeDasharray="276.46"
                    strokeDashoffset={276.46 * (1 - (balance?.recovery_rate ?? (isPaid ? 1 : 0)))}
                    strokeLinecap="round"
                    style={{ transition: "stroke-dashoffset 0.8s ease" }}
                  />
                </svg>
                <span className={`font-serif text-xl font-semibold relative z-10 ${
                  isPaid ? "text-emerald-700" : "text-amber-700"
                }`}>
                  {CURRENT_YEAR}
                </span>
              </div>

              <div className="text-center">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-1">
                  Cotisation annuelle
                </p>
                <p className={`text-sm font-medium ${isPaid ? "text-emerald-700" : "text-amber-700"}`}>
                  {isPaid ? "Réglée · Merci ✦" : isPartial ? "Partiellement réglée" : "En attente de règlement"}
                </p>
              </div>
            </div>

            <Button
              className={`w-full rounded-full py-5 text-sm font-medium tracking-wide transition-all duration-300 group ${
                isPaid
                  ? "bg-slate-900 hover:bg-emerald-800 text-white"
                  : "bg-emerald-700 hover:bg-emerald-800 text-white shadow-lg shadow-emerald-900/15"
              }`}
              onClick={() => navigate("/mon-dossier")}
            >
              {isPaid ? "Voir mon dossier" : "Compléter et payer"}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </div>

        {/* Payment history */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-100">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-6">
            Historique des cotisations
          </p>

          {filteredPayments.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {filteredPayments.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between py-4 gap-4 group"
                >
                  {/* Year */}
                  <span className="font-serif text-2xl text-slate-300 w-14 flex-shrink-0 group-hover:text-emerald-600 transition-colors">
                    {p.year}
                  </span>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 capitalize">
                      {p.membership_type === "adhesion"
                        ? "Cotisation Annuelle"
                        : p.membership_type}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {new Date(p.created_at).toLocaleDateString("fr-FR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </p>
                  </div>

                  {/* Amount + status */}
                  <div className="text-right flex-shrink-0">
                    <p className="font-serif text-lg text-slate-900">
                      {p.amount.toFixed(2)}{" "}
                      <span className="text-sm font-sans text-slate-400">{p.currency}</span>
                    </p>
                    <span
                      className={`inline-block text-[10px] font-semibold uppercase tracking-widest mt-0.5 ${
                        p.status === "completed"
                          ? "text-emerald-600"
                          : "text-amber-600"
                      }`}
                    >
                      {p.status === "completed" ? "Réglée" : "En attente"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center py-12 gap-3">
              <div className="h-12 w-12 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-slate-300" />
              </div>
              <p className="text-sm text-slate-400 font-light text-center">
                Aucune transaction pour le moment.
              </p>
              <Button
                variant="outline"
                className="mt-2 rounded-full border-slate-200 text-slate-600 hover:bg-slate-50 text-sm px-6"
                onClick={() => navigate("/mon-dossier")}
              >
                Démarrer mon adhésion
              </Button>
            </div>
          )}
        </div>

      </main>

      {/* Footer minimal */}
      <footer className="py-6 text-center border-t border-slate-200/60">
        <p className="text-xs text-slate-400 font-light">
          &copy; {new Date().getFullYear()} Hafani Management and Solutions
        </p>
      </footer>

    </div>
  );
};
